import { omit } from 'lodash';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import { initCloudinaryStreaming } from 'redux-store/reducers/cloudinaryVideos';

import { createReducer } from 'utils/redux';
import { API, destroy, put } from 'utils/api';
import { logError } from 'utils/log';
import { CLOUDINARY_VIDEO_UPLOAD } from 'utils/cloudinary';
import { CAMPAIGN_ASSET_VIDEO } from 'utils/campaignAsset';
import { __t, applyTranslations } from 'utils/language';
import {
  uploadImages as uploadCampaignImages,
  uploadVideos as uploadCampaignVideos,
  uploadImage
} from 'utils/upload-asset';
import { API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED } from 'utils/status';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./i18n/campaign-assets-reducer.i18n.json'));

const { CAMPAIGNS } = API.END_POINTS;
const { IMAGES } = API.END_POINTS.API;

const ADD_ASSETS = 'campaignAssets/ADD_ASSETS';
const RECEIVE_ASSETS = 'campaignAssets/RECEIVE_ASSETS';
const REMOVE_ASSET = 'campaignAssets/REMOVE_ASSET';
const RESET_ASSETS = 'campaignAssets/RESET_ASSETS';
const SET_ASSETS = 'campaignAssets/SET_ASSETS';
const SET_NEW_MAIN_IMAGE = 'campaignAssets/SET_NEW_MAIN_IMAGE';
const REMOVE_MAIN_IMAGE = 'campaignAssets/REMOVE_MAIN_IMAGE';

const initialState = {
  assets: [],
  mainImage: null,
  isUpdated: false,
  isMainImageRemoved: false
};

export function receiveAssets(assets, mainImage) {
  return {
    type: RECEIVE_ASSETS,
    payload: {
      assets: assets || [],
      mainImage: mainImage || null
    }
  };
}

export function resetAssets() {
  return {
    type: RESET_ASSETS
  };
}

export function removeAsset(asset) {
  return {
    type: REMOVE_ASSET,
    payload: {
      asset
    }
  };
}

function addAssets(assets) {
  return {
    type: ADD_ASSETS,
    payload: {
      assets
    }
  };
}

export function setCampaignAssets(assets) {
  return {
    type: SET_ASSETS,
    payload: {
      assets
    }
  };
}

function setNewMainImage(imageAsset) {
  return {
    type: SET_NEW_MAIN_IMAGE,
    payload: {
      mainImage: imageAsset
    }
  };
}

function removeMainImage() {
  return {
    type: REMOVE_MAIN_IMAGE
  };
}

function addTempIds(assets) {
  const datetime = (new Date()).getTime(); // `datetime` and `index` are helpful for the correct sorting
  return assets.map((a, index) => ({
    ...a,
    isNew: true, // indicator, that this image is new and `id` is temporary
    // FE uses a temp id before we save a brief and get real `id` from API
    id: `temp-${datetime}-${index}-${a.assetType}`
  }));
}

export function addLinks(links) {
  return (dispatch) => {
    const assets = links.map(link => ({
      assetType: CAMPAIGN_ASSET_VIDEO,
      url: link
    }));

    const newAssets = addTempIds(assets);
    dispatch(addAssets(newAssets));

    return Promise.resolve();
  };
}

export function uploadImages(images) {
  const url = IMAGES;

  return (dispatch) => {
    dispatch(showLoading(url));

    return uploadCampaignImages(images)
      .then((uploadedImages) => {
        const newAssets = addTempIds(uploadedImages);
        dispatch(addAssets(newAssets));

        dispatch(hideLoading(url));

        return newAssets;
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        dispatch(danger(error.message));

        throw error;
      });
  };
}

export function uploadVideos(videos) {
  const url = CLOUDINARY_VIDEO_UPLOAD;

  return (dispatch, getState) => {
    const { runtimeConfig } = getState();
    dispatch(showLoading(url));

    return uploadCampaignVideos(videos, Boolean(runtimeConfig.config.userS3VideoUpload))
      .then((uploadedVideos) => {
        const newAssets = addTempIds(uploadedVideos);
        dispatch(addAssets(newAssets));
        if (runtimeConfig.config.userS3VideoUpload) {
          dispatch(initCloudinaryStreaming(newAssets));
        }

        dispatch(hideLoading(url));

        return newAssets;
      })
      .catch((error) => {
        logError(error);
        dispatch(hideLoading(url));

        if (error?.response?.data?.code === API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED) {
          dispatch(danger(__t('collabary_common.errors.content_type_is_not_supported')));
        }
      });
  };
}

export function setMainImage(image) {
  return (dispatch) => {
    const url = IMAGES;

    dispatch(showLoading(url));

    uploadImage(image)
      .then((uploadedImage) => {
        const newAsset = addTempIds([uploadedImage])[0];
        return dispatch(setNewMainImage(newAsset));
      })
      .catch(error => {
        const code = error?.response?.data?.code ?? null;
        const details = error?.response?.data?.detail ?? null;

        logError(error);

        if (code === 'upload.unsupported.error' && details !== null) {
          dispatch(danger(details));
        } else {
          dispatch(danger(__t('campaign_assets.errors.campaign_upload_images_error')));
        }
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function unsetMainImage() {
  return (dispatch) => dispatch(removeMainImage());
}

// put or delete
function updateMainImage(campaignId, imageAsset) {
  const url = CAMPAIGNS.updateCampaignMainImage(campaignId);

  const request = imageAsset ? put : destroy;

  return (dispatch) => {
    dispatch(showLoading(url));

    return request(url, { data: imageAsset })
      .then(() => {
        dispatch(hideLoading(url));
      })
      .catch(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function saveAssets(campaignId) {
  const url = CAMPAIGNS.updateCampaignAssets(campaignId);

  return (dispatch, getState) => {
    const { campaignAssets } = getState();
    const assets = campaignAssets.assets;

    let newMainImageAsset = null;

    if (campaignAssets.mainImage && campaignAssets.mainImage.isNew) {
      newMainImageAsset = omit(campaignAssets.mainImage, ['id', 'isNew']);
    }

    dispatch(showLoading(url));

    const assetsPayload = assets.map(asset => {
      let assetPayload;

      if (asset.isNew) {
        assetPayload = omit(asset, ['id', 'isNew']);
      } else {
        assetPayload = omit(asset, ['isNew']); // keep ids
      }

      return assetPayload;
    });

    return put(url, { data: assetsPayload })
      .then(() => {
        if (newMainImageAsset || campaignAssets.isMainImageRemoved) {
          return dispatch(updateMainImage(campaignId, newMainImageAsset));
        }

        return Promise.resolve();
      })
      .then(() => {
        dispatch(hideLoading(url));
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        logError(error);
        dispatch(danger(__t('campaign_assets.errors.campaign_update_assets_error')));
      });
  };
}

export default createReducer('campaignAssets', initialState, {
  [RESET_ASSETS]: () => initialState,
  [RECEIVE_ASSETS]: (state, { assets, mainImage }) => ({
    ...state,
    assets,
    mainImage,
    isMainImageRemoved: false,
    isUpdated: false
  }),
  [ADD_ASSETS]: (state, { assets }) => ({
    ...state,
    assets: [...state.assets, ...assets],
    isUpdated: true
  }),
  [SET_ASSETS]: (state, { assets }) => ({
    ...state,
    assets,
    isUpdated: true
  }),
  [REMOVE_ASSET]: (state, { asset }) => ({
    ...state,
    assets: state.assets.filter(a => a.id !== asset.id),
    isUpdated: true
  }),
  [SET_NEW_MAIN_IMAGE]: (state, { mainImage }) => ({
    ...state,
    mainImage,
    isMainImageRemoved: false,
    isUpdated: true
  }),
  [REMOVE_MAIN_IMAGE]: (state) => ({
    ...state,
    mainImage: null,
    isMainImageRemoved: true,
    isUpdated: true
  })
});
